import * as bookmarkConstants from "../../../../constants/bookmarkConstants";
import * as dataTypeConstants from "../../../../constants/dataTypeConstants";
import * as generalHelper from "../../../../helpers/generalHelper";
import * as modalTypes from "../../../../constants/modalTypes";
import * as objectTypes from "../../../../constants/objectTypes";
import * as optionHelper from "../../../../helpers/optionHelper";
import * as propertyConstants from "../../../../constants/propertyConstants";
import * as translationHelper from "../../../../helpers/translationHelper";

import { Product, createEmptyProduct, generateProductData, generateProductHeadings } from "../../../../types/product";
import React, { Component } from "react";

import { Bookmark } from "../../../../types/bookmark";
import { FileCategory } from "../../../../types/fileCategory";
import { ModalParams } from "../../../../types/modalParams";
import { ModalProperties } from "../../../../types/modalProperties";
import { TableContainer } from "../../../Table";
import { TableHeading } from "../../../../types/tableHeading";

import { t as translate } from "react-i18nify";

type Props = {
    tableConstant: string;
    objectType: objectTypes.ObjectType;
    dataPrivileges: any;
    loading: boolean;
    buttonLoading: boolean;
    fileCategoryList: Array<FileCategory>;
    allList: Array<Product>;
    activeList: Array<Product>;
    columnOrder: Array<propertyConstants.Property>;
    columnVisibility: Record<propertyConstants.Property, boolean>;
    columnWidth: Record<propertyConstants.Property, number>;
    offset: number;
    page: number;
    rowCount: number;
    rowCountCustom: boolean;
    search: string | null;
    searchParams: Record<propertyConstants.Property, string>;
    showFilterRow: boolean;
    showGlobalSearch: boolean;
    sortingAsc: boolean;
    sortingCriterion: propertyConstants.Property | null;
    totalCount: number | null;
    useTmcNames: boolean;
};

export class MasterProductTable extends Component<Props> {
    generateTableHeadings = (
        modalType: modalTypes.ModalType | null = null,
        isDuplicateBookmark: boolean = false
    ): Array<TableHeading> => {
        const propertyList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_NAME,
            propertyConstants.PROPERTY_SEARCH_NAME,
            propertyConstants.PROPERTY_PRINT_NAME,
            propertyConstants.PROPERTY_CODE,
            propertyConstants.PROPERTY_SEARCH_CODE,
            propertyConstants.PROPERTY_MORE_INFO,
            propertyConstants.PROPERTY_UUID,
            propertyConstants.PROPERTY_PRIORITY
        ];
        const requiredList: Array<propertyConstants.Property> = [propertyConstants.PROPERTY_NAME];
        const alwaysVisibleList: Array<propertyConstants.Property> = [propertyConstants.PROPERTY_NAME];
        let editableList: Array<propertyConstants.Property> = [...propertyList];
        let visibleList: Array<propertyConstants.Property> = [...propertyList];

        visibleList.splice(6, 0, propertyConstants.PROPERTY_IMAGE_ID);

        if (modalType === null) {
            visibleList.splice(7, 0, propertyConstants.PROPERTY_FILES);
        }

        if (modalType !== null) {
            editableList.push(propertyConstants.PROPERTY_IMAGE_ID);
        }

        if (this.props.useTmcNames) {
            visibleList.splice(2, 0, propertyConstants.PROPERTY_TMC_NAME);
            editableList.push(propertyConstants.PROPERTY_TMC_NAME);
        }

        if (isDuplicateBookmark) {
            visibleList = [propertyConstants.PROPERTY_DATA_TYPE_LIST];
            editableList = [propertyConstants.PROPERTY_DATA_TYPE_LIST];
        }

        let orderList = [...visibleList];

        if (modalType === null) {
            orderList = optionHelper.handleTableHeadingOrder(visibleList, this.props.columnOrder);
            visibleList = optionHelper.handleTableHeadingVisibility(visibleList, this.props.columnVisibility);
        }

        return generateProductHeadings(
            this.props.objectType,
            generalHelper.generateDataTypes(this.props.objectType, dataTypeConstants.MASTER_PRODUCT_DATA_TYPE_LIST),
            orderList,
            visibleList,
            editableList,
            requiredList,
            alwaysVisibleList,
            this.props.columnWidth
        );
    };

    generateBookmarks = (modalType: modalTypes.ModalType, isDuplicatedModal: boolean = false): Array<Bookmark> => {
        const bookmarks = [
            new Bookmark(
                bookmarkConstants.BOOKMARK_GENERAL,
                bookmarkConstants.BOOKMARK_TYPE_FORM,
                translate("general.general"),
                this.generateTableHeadings(modalType),
                this.props.dataPrivileges, // TODO own privilege
                false
            ),
            new Bookmark(
                bookmarkConstants.BOOKMARK_FILES,
                bookmarkConstants.BOOKMARK_TYPE_TABLE,
                translate("product.productFiles"),
                [],
                this.props.dataPrivileges, // TODO own privilege
                false
            )
        ];

        if (isDuplicatedModal) {
            bookmarks.push(
                new Bookmark(
                    bookmarkConstants.BOOKMARK_DUPLICATE,
                    bookmarkConstants.BOOKMARK_TYPE_FORM_TABLE,
                    translate("general.duplicate"),
                    this.generateTableHeadings(modalType, true),
                    this.props.dataPrivileges, // TODO own privilege
                    false
                )
            );
        }

        return bookmarks;
    };

    generateAddModalParams = (): ModalParams => {
        return new ModalParams(
            modalTypes.ADD_MODAL,
            this.props.objectType,
            translate("product.addProduct"),
            createEmptyProduct(),
            [],
            this.generateBookmarks(modalTypes.MODAL_PRODUCTS_ADD)
        );
    };

    generateEditModalParams = (): ModalParams | null => {
        const activeData = generateProductData(this.props.objectType, this.props.activeList);

        if (activeData.length) {
            return new ModalParams(
                modalTypes.EDIT_MODAL,
                this.props.objectType,
                translate("product.editProduct"),
                activeData[0],
                [],
                this.generateBookmarks(modalTypes.MODAL_PRODUCTS_EDIT)
            );
        }

        return null;
    };

    generateDuplicateModalParams = (): ModalParams | null => {
        const activeData = generateProductData(
            objectTypes.MASTER_PRODUCT_DUPLICATE,
            this.props.activeList,
            dataTypeConstants.MASTER_PRODUCT_DATA_TYPE_LIST,
            true
        );

        if (activeData.length) {
            return new ModalParams(
                modalTypes.DUPLICATE_MODAL,
                objectTypes.MASTER_PRODUCT_DUPLICATE,
                translate("product.duplicateProduct"),
                activeData[0],
                undefined,
                this.generateBookmarks(modalTypes.MODAL_PRODUCTS_DUPLICATE, true),
                undefined,
                undefined,
                undefined,
                this.props.dataPrivileges,
                undefined,
                undefined
            );
        }

        return null;
    };

    render(): JSX.Element {
        const modalProperties: ModalProperties = {
            addModalType: modalTypes.MODAL_PRODUCTS_ADD,
            addModalParams: this.generateAddModalParams(),
            duplicateModalType: modalTypes.MODAL_PRODUCTS_DUPLICATE,
            duplicateModalParams: this.generateDuplicateModalParams(),
            editModalType: modalTypes.MODAL_PRODUCTS_EDIT,
            editModalParams: this.generateEditModalParams()
        };

        return (
            <div className="height-100">
                <TableContainer
                    className={"table-with-filter"}
                    tableConstant={this.props.tableConstant}
                    objectType={this.props.objectType}
                    dataPrivileges={this.props.dataPrivileges}
                    loading={this.props.loading}
                    buttonLoading={this.props.buttonLoading}
                    title={translationHelper.getTitleSectionTranslation(this.props.objectType)}
                    titleClassName={"main-title"}
                    headings={this.generateTableHeadings()}
                    data={generateProductData(this.props.objectType, this.props.allList)}
                    activeData={this.props.activeList}
                    modalProperties={modalProperties}
                    offset={this.props.offset}
                    page={this.props.page}
                    rowCount={this.props.rowCount}
                    rowCountCustom={this.props.rowCountCustom}
                    search={this.props.search}
                    searchParams={this.props.searchParams}
                    showFilterRow={this.props.showFilterRow}
                    showGlobalSearch={this.props.showGlobalSearch}
                    sortingAsc={this.props.sortingAsc}
                    sortingCriterion={this.props.sortingCriterion}
                    totalCount={this.props.totalCount}
                />
            </div>
        );
    }
}
